import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Image, Save, Eye, EyeOff, FileText, Settings, Share2, Plus, Trash2 } from "lucide-react";

export default function AdminContent() {
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [socials, setSocials] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [tab, setTab] = useState("homepage");
  const [editing, setEditing] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({ title: "", content: "", enabled: true, show_in_footer: false });
  const [siteForm, setSiteForm] = useState({ site_title: "", seo_description: "", seo_keywords: "", delete_account_warning: "" });
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, pRes, sRes, soRes] = await Promise.all([
          API.get("/admin/content"),
          API.get("/admin/pages"),
          API.get("/admin/site-settings"),
          API.get("/admin/social-links").catch(() => ({ data: { links: [] } })),
        ]);
        setSections(cRes.data.sections);
        setPages(pRes.data.pages || []);
        setSocials(soRes.data.links || []);
        const s = sRes.data;
        setSiteSettings(s);
        setSiteForm({ site_title: s.site_title || "CrystoBag", seo_description: s.seo_description || "", seo_keywords: s.seo_keywords || "", logo_url: s.logo_url || "", favicon_url: s.favicon_url || "", delete_account_warning: s.delete_account_warning || "" });
      } catch {}
    };
    load();
  }, []);

  const startEdit = (section) => {
    setEditing(section.section_id);
    setForm({
      title: section.title || "",
      description: section.description || "",
      image_url: section.image_url || "",
      button_text: section.button_text || "",
      button_link: section.button_link || "",
      visible: section.visible !== false,
    });
  };

  const save = async (sectionId) => {
    setLoading(sectionId);
    try {
      await API.put(`/admin/content/${sectionId}`, form);
      toast.success("Section updated");
      const { data } = await API.get("/admin/content");
      setSections(data.sections);
      setEditing(null);
    } catch (e) { toast.error("Failed to update"); }
    finally { setLoading(""); }
  };

  const savePage = async (slug) => {
    setLoading(`page-${slug}`);
    try {
      await API.put(`/admin/pages/${slug}`, pageForm);
      toast.success("Page updated");
      const { data } = await API.get("/admin/pages");
      setPages(data.pages || []);
      setEditingPage(null);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const saveSiteSettings = async () => {
    setLoading("site");
    try {
      await API.put("/admin/site-settings", siteForm);
      toast.success("Site settings saved");
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  return (
    <AdminLayout title="Content Management">
      <div className="space-y-4" data-testid="admin-content">
        {/* Tabs */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {[["homepage", "Homepage"], ["pages", "Pages"], ["socials", "Social Media"], ["site", "Site Settings"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`content-tab-${key}`}
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === key ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#64748B]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* HOMEPAGE TAB — all existing sections grouped */}
        {tab === "homepage" && (<>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#94A3B8]">Edit all homepage sections (hero, steps, stats, features). Changes appear immediately.</p>
          <button onClick={async () => {
            const sid = prompt("Section ID (e.g. stat_5, feature_7, step_6, custom_intro):");
            if (!sid) return;
            try {
              await API.post("/admin/content", { section_id: sid, title: "New Section", description: "" });
              toast.success("Section created");
              const { data } = await API.get("/admin/content"); setSections(data.sections);
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }} data-testid="create-section-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90">
            + New Section
          </button>
        </div>
        {sections.map((section) => (
          <div key={section.section_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid={`content-section-${section.section_id}`}>
            <div className="flex items-center justify-between p-4 border-b border-[#2A3241]/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                  <Image className="w-4 h-4 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#F8FAFC]">{section.section_id}</p>
                  <p className="text-xs text-[#64748B]">Order: {section.order} | {section.visible ? "Visible" : "Hidden"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {editing === section.section_id ? (
                  <button onClick={() => save(section.section_id)} disabled={loading === section.section_id} data-testid={`save-content-${section.section_id}`}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg hover:bg-[#10B981]/90 disabled:opacity-50">
                    <Save className="w-3 h-3" /> {loading === section.section_id ? "..." : "Save"}
                  </button>
                ) : (
                  <button onClick={() => startEdit(section)} data-testid={`edit-content-${section.section_id}`}
                    className="px-3 py-1.5 text-xs font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">
                    Edit
                  </button>
                )}
                <button onClick={async () => {
                  if (!window.confirm(`Delete section "${section.section_id}"?`)) return;
                  try { await API.delete(`/admin/content/${section.section_id}`); toast.success("Deleted"); const { data } = await API.get("/admin/content"); setSections(data.sections); } catch { toast.error("Failed"); }
                }} className="p-1.5 text-[#64748B] hover:text-[#EF4444] rounded">
                  <EyeOff className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {editing === section.section_id ? (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Title</Label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Image URL</Label>
                    <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 h-9 text-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 text-sm" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Button Text</Label>
                    <Input value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })}
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Button Link</Label>
                    <Input value={form.button_link} onChange={(e) => setForm({ ...form, button_link: e.target.value })}
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 h-9 text-sm" />
                  </div>
                  <div className="flex items-end">
                    <button onClick={() => setForm({ ...form, visible: !form.visible })} data-testid={`toggle-visibility-${section.section_id}`}
                      className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase rounded-lg border ${form.visible ? "border-[#10B981]/30 text-[#10B981] bg-[#10B981]/10" : "border-[#EF4444]/30 text-[#EF4444] bg-[#EF4444]/10"}`}>
                      {form.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {form.visible ? "Visible" : "Hidden"}
                    </button>
                  </div>
                </div>
                {form.image_url && (
                  <div className="mt-2">
                    <p className="text-xs text-[#64748B] mb-1">Preview:</p>
                    <img src={form.image_url} alt="preview" className="h-24 rounded-lg object-cover border border-[#2A3241]" />
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 flex items-center gap-4">
                {section.image_url && <img src={section.image_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#2A3241]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#F8FAFC] truncate">{section.title}</p>
                  <p className="text-xs text-[#94A3B8] truncate">{section.description?.slice(0, 80)}...</p>
                </div>
              </div>
            )}
          </div>
        ))}
        {sections.length === 0 && <p className="text-[#94A3B8] text-center py-8">No content sections found</p>}
        </>)}

        {/* PAGES TAB — Privacy, Contact, etc */}
        {tab === "pages" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#94A3B8]">Manage static pages (Privacy Policy, Contact Us, etc). Content supports HTML.</p>
              <button onClick={async () => {
                const slug = prompt("Page slug (e.g. terms, about, faq):");
                if (!slug) return;
                try {
                  await API.post("/admin/pages", { slug, title: slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) });
                  toast.success("Page created");
                  const { data } = await API.get("/admin/pages"); setPages(data.pages || []);
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }} className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90">
                + New Page
              </button>
            </div>
            {pages.map(pg => (
              <div key={pg.slug} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4" data-testid={`page-admin-${pg.slug}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-[#B026FF]" />
                    <div>
                      <p className="text-sm font-bold text-[#F8FAFC]">{pg.title || pg.slug}</p>
                      <p className="text-xs text-[#64748B]">/page/{pg.slug} | {pg.enabled ? "Enabled" : "Disabled"} | Footer: {pg.show_in_footer ? "Yes" : "No"}</p>
                    </div>
                  </div>
                  <button onClick={() => { setEditingPage(editingPage === pg.slug ? null : pg.slug); setPageForm({ title: pg.title || "", content: pg.content || "", enabled: pg.enabled !== false, show_in_footer: pg.show_in_footer || false }); }}
                    className="px-3 py-1.5 text-xs font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">
                    {editingPage === pg.slug ? "Cancel" : "Edit"}
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm(`Delete page "${pg.slug}"?`)) return;
                    try { await API.delete(`/admin/pages/${pg.slug}`); toast.success("Deleted"); const { data } = await API.get("/admin/pages"); setPages(data.pages || []); } catch { toast.error("Failed"); }
                  }} className="p-1.5 text-[#64748B] hover:text-[#EF4444] rounded">
                    <EyeOff className="w-3.5 h-3.5" />
                  </button>
                </div>
                {editingPage === pg.slug && (
                  <div className="mt-3 pt-3 border-t border-[#2A3241]/30 space-y-3">
                    <div><Label className="text-[#94A3B8] text-xs">Title</Label><Input value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                    <div><Label className="text-[#94A3B8] text-xs">Content (HTML supported)</Label><Textarea value={pageForm.content} onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })} rows={8} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] text-sm mt-1 font-mono-game" /></div>
                    <div className="flex gap-2">
                      <button onClick={() => savePage(pg.slug)} disabled={loading === `page-${pg.slug}`} className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" />Save</button>
                      <button onClick={() => setPageForm({ ...pageForm, enabled: !pageForm.enabled })} className={`px-3 py-2 text-xs font-bold uppercase rounded-lg border ${pageForm.enabled ? "border-[#10B981]/30 text-[#10B981]" : "border-[#EF4444]/30 text-[#EF4444]"}`}>
                        {pageForm.enabled ? <><Eye className="w-3 h-3 inline mr-1" />Enabled</> : <><EyeOff className="w-3 h-3 inline mr-1" />Disabled</>}
                      </button>
                      <button onClick={() => setPageForm({ ...pageForm, show_in_footer: !pageForm.show_in_footer })} className={`px-3 py-2 text-xs font-bold uppercase rounded-lg border ${pageForm.show_in_footer ? "border-[#00F0FF]/30 text-[#00F0FF]" : "border-[#2A3241] text-[#64748B]"}`}>
                        Footer: {pageForm.show_in_footer ? "YES" : "NO"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* SOCIAL MEDIA TAB */}
        {tab === "socials" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#94A3B8]">Manage social media links shown in the footer.</p>
              <button onClick={async () => {
                const name = prompt("Platform name (e.g. Reddit, Twitch):");
                if (!name) return;
                try { await API.post("/admin/social-links", { platform: name }); toast.success("Added"); const { data } = await API.get("/admin/social-links"); setSocials(data.links); } catch { toast.error("Failed"); }
              }} className="px-4 py-2 text-xs font-bold uppercase bg-[#00F0FF] text-[#0B0E14] rounded-lg"><Plus className="w-3 h-3 inline mr-1" /> Custom</button>
            </div>
            {socials.map(s => (
              <div key={s.link_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center gap-4" data-testid={`social-${s.link_id}`}>
                <div className="w-10 h-10 rounded-lg bg-[#1C212E] flex items-center justify-center text-sm font-bold text-[#94A3B8]">
                  {s.platform?.charAt(0) || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#F8FAFC]">{s.platform}</p>
                  <Input value={s.url || ""} placeholder={`https://${(s.platform || "").toLowerCase()}.com/...`}
                    onChange={(e) => setSocials(prev => prev.map(x => x.link_id === s.link_id ? { ...x, url: e.target.value } : x))}
                    onBlur={(e) => API.put(`/admin/social-links/${s.link_id}`, { url: e.target.value }).catch(() => {})}
                    className="bg-[#0B0E14] border-[#2A3241] text-[#00F0FF] h-8 text-xs mt-1 font-mono-game" />
                </div>
                <button onClick={async () => {
                  const newVal = !s.enabled;
                  setSocials(prev => prev.map(x => x.link_id === s.link_id ? { ...x, enabled: newVal } : x));
                  await API.put(`/admin/social-links/${s.link_id}`, { enabled: newVal });
                }} className={`px-3 py-1.5 text-[10px] font-bold uppercase rounded-lg border ${s.enabled ? "border-[#10B981]/30 text-[#10B981] bg-[#10B981]/10" : "border-[#2A3241] text-[#64748B]"}`}>
                  {s.enabled ? "ON" : "OFF"}
                </button>
                <button onClick={async () => {
                  await API.delete(`/admin/social-links/${s.link_id}`);
                  setSocials(prev => prev.filter(x => x.link_id !== s.link_id));
                }} className="p-1.5 text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        {/* SITE SETTINGS TAB */}
        {tab === "site" && (
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">Global site settings — title, logo, SEO meta tags.</p>
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
              <div className="flex items-center gap-2"><Settings className="w-5 h-5 text-[#FFD600]" /><h3 className="text-lg font-bold text-[#F8FAFC]">Site Settings</h3></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-[#94A3B8] text-sm">Website Title</Label><Input value={siteForm.site_title} onChange={(e) => setSiteForm({ ...siteForm, site_title: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="site-title-input" /></div>
                <div><Label className="text-[#94A3B8] text-sm">Logo URL</Label><Input value={siteForm.logo_url || ""} onChange={(e) => setSiteForm({ ...siteForm, logo_url: e.target.value })} placeholder="https://..." className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="site-logo-input" /></div>
                <div><Label className="text-[#94A3B8] text-sm">Favicon URL (browser tab icon)</Label><Input value={siteForm.favicon_url || ""} onChange={(e) => setSiteForm({ ...siteForm, favicon_url: e.target.value })} placeholder="https://...icon.png" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="site-favicon-input" /></div>
              </div>
              <div><Label className="text-[#94A3B8] text-sm">SEO Description (meta tag)</Label><Textarea value={siteForm.seo_description} onChange={(e) => setSiteForm({ ...siteForm, seo_description: e.target.value })} rows={2} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] text-sm mt-1" data-testid="site-seo-desc" /></div>
              <div><Label className="text-[#94A3B8] text-sm">SEO Keywords (comma-separated)</Label><Input value={siteForm.seo_keywords} onChange={(e) => setSiteForm({ ...siteForm, seo_keywords: e.target.value })} placeholder="crypto, game, earn, mining" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="site-seo-keywords" /></div>
              <div><Label className="text-[#94A3B8] text-sm">Delete Account Warning (shown to users in Profile → Danger Zone)</Label><Textarea value={siteForm.delete_account_warning} onChange={(e) => setSiteForm({ ...siteForm, delete_account_warning: e.target.value })} rows={3} placeholder="This will permanently delete your account, all your coins, resources, workers, inventory, deposits, withdrawals history, and referral data. This action cannot be undone." className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] text-sm mt-1" data-testid="site-delete-warning" /></div>
              <button onClick={saveSiteSettings} disabled={loading === "site"} data-testid="save-site-settings"
                className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50">
                <Save className="w-4 h-4" /> {loading === "site" ? "Saving..." : "Save Site Settings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
